import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProfileStore, getFacilityKeyForPortal } from "@/store/profile";
import authRequest from "@/api/auth-request";
import { cn } from "@/utils/cn";
import { ProfileInfo } from "@/components/profile/ProfileInfo";
import type { DemographicsFormHandle, ProfileEditActions } from "@/components/demographics/DemographicsForm";
import { LoginInfo } from "../components/profile/LoginInfo";
import { CommInfo } from "@/components/profile/CommInfo";
import { PaymentInfo } from "@/components/profile/PaymentInfo";
import {
  type ProfileTab,
  getVisibleProfileTabs,
} from "@/config/profileNav";

export type { ProfileTab } from "@/config/profileNav";

export function ProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const profileAuthInfo = useProfileStore((s) => s.profileAuthInfo);
  const facilityInfo = useProfileStore((s) => s.facilityInfo);
  const currentProfilePageView = useProfileStore((s) => s.currentProfilePageView);
  const setProfileCurrentPage = useProfileStore((s) => s.setProfileCurrentPage);
  const setFacilityInfo = useProfileStore((s) => s.setFacilityInfo);

  const canProcess = !!(facilityInfo?.openEdge || facilityInfo?.fortis);
  const pageParam = (searchParams.get("page") as ProfileTab) || "MyInfo";
  const currentPage: ProfileTab = (currentProfilePageView as ProfileTab) || pageParam;
  const visibleTabs = getVisibleProfileTabs(profileAuthInfo, canProcess);

  useEffect(() => {
    const page = (searchParams.get("page") as ProfileTab) || "MyInfo";
    setProfileCurrentPage(page);
    const key = getFacilityKeyForPortal();
    if (key) {
      authRequest.request.getFacilityInfo(key).then((res) => {
        setFacilityInfo(res.data ?? null);
      }).catch(console.error);
    }
  }, [currentPatient?.facilityID, searchParams, setProfileCurrentPage, setFacilityInfo]);

  const [loginInfoDirty, setLoginInfoDirty] = useState(false);
  const [myInfoEditActions, setMyInfoEditActionsState] = useState<ProfileEditActions | null>(null);
  const [pendingTab, setPendingTab] = useState<ProfileTab | null>(null);
  const [showMyInfoDiscardModal, setShowMyInfoDiscardModal] = useState(false);

  const myInfoFormRef = useRef<DemographicsFormHandle>(null);
  const setMyInfoEditActions = useCallback((actions: ProfileEditActions) => {
    setMyInfoEditActionsState(actions);
  }, []);

  const setPage = (page: ProfileTab) => {
    if (currentPage === "LoginInfo" && loginInfoDirty && page !== "LoginInfo") {
      setPendingTab(page);
      return;
    }
    if (currentPage === "MyInfo" && page !== "MyInfo") {
      const form = myInfoFormRef.current;
      const dirty = form?.isDirty() ?? myInfoEditActions?.hasDirty ?? false;
      const editing = form?.isEditing() ?? myInfoEditActions?.editing ?? false;
      if (dirty || editing) {
        setPendingTab(page);
        return;
      }
    }
    setSearchParams({ page });
    setProfileCurrentPage(page);
  };

  const confirmLeaveLoginInfo = () => {
    if (pendingTab) {
      setSearchParams({ page: pendingTab });
      setProfileCurrentPage(pendingTab);
      setPendingTab(null);
    }
  };

  const cancelLeaveLoginInfo = () => setPendingTab(null);

  const currentTabLabel =
    visibleTabs.find((t) => t.id === currentPage)?.label ?? "Profile";

  const myInfoHeaderActions = currentPage === "MyInfo" && myInfoEditActions?.ready && (
    <div className="ml-0 flex shrink-0 items-center gap-2 sm:pr-0 md:ml-auto md:pr-3">
      {!myInfoEditActions.editing ? (
        <button
          type="button"
          onClick={myInfoEditActions.startEdit}
          className="-mr-3 flex min-h-11 items-center gap-1.5 px-3 text-sm font-semibold rounded-md text-healthsource-navy transition-colors hover:bg-[var(--hs-tint)] sm:mr-0"
        >
          <i className="fas fa-pen" />
          Edit
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={() => (myInfoEditActions.hasDirty ? setShowMyInfoDiscardModal(true) : myInfoEditActions.cancel())}
            className="flex min-h-11 items-center gap-1.5 px-3 text-sm font-semibold rounded-md text-healthsource-gray transition-colors hover:bg-[var(--hs-tint)] hover:text-healthsource-dark"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => myInfoEditActions.save()}
            disabled={myInfoEditActions.saving}
            className="min-h-11 rounded-md px-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: "var(--hs-red)" }}
          >
            {myInfoEditActions.saving ? "Saving…" : "Update"}
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-3">
        <div className="flex items-center">
          <div
            className="mr-3 h-7 w-1 rounded-sm bg-healthsource-navy"
          />
          <h2 className="text-xl font-extrabold text-healthsource-dark">
            Profile
          </h2>
        </div>

        {/* Below sm the card is flattened onto the page background so the tab content runs to the page gutter. */}
        <div className="sm:overflow-hidden sm:rounded-xl sm:border sm:border-healthsource-lightLavender sm:bg-white sm:shadow-[0_2px_8px_rgba(30,37,43,0.06)]">
          <nav
            className="flex flex-1 flex-col border-b border-healthsource-lightLavender md:flex-row md:items-stretch"
            aria-label="Profile tabs"
          >
            {/* Mobile: show current section name; tab switching is in the hamburger only */}
            <div className="flex w-full min-h-[48px] min-w-0 items-center justify-between gap-2 py-0.5 sm:px-3 md:hidden">
              <span className="min-w-0 text-sm font-bold text-healthsource-dark">
                {currentTabLabel}
              </span>
              {myInfoHeaderActions}
            </div>
            {/* md+: horizontal tabs */}
            <div className="hidden w-full min-w-0 grow items-center md:flex">
              <div className="flex min-w-0 flex-1 overflow-x-auto">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setPage(tab.id)}
                    className={cn(
                      "relative shrink-0 px-5 py-3.5 text-[13px] font-bold transition-colors",
                      currentPage === tab.id
                        ? "text-healthsource-dark"
                        : "text-healthsource-gray hover:text-healthsource-dark"
                    )}
                  >
                    {tab.label}
                    {currentPage === tab.id && (
                      <span
                        className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-sm bg-healthsource-navy"
                      />
                    )}
                  </button>
                ))}
              </div>
              {myInfoHeaderActions}
            </div>
          </nav>

          <div className="py-3 sm:p-3">
            {currentPage === "MyInfo" && (
              <ProfileInfo
                ref={myInfoFormRef}
                onEditActions={setMyInfoEditActions}
              />
            )}
            {currentPage === "LoginInfo" && <LoginInfo onDirtyChange={setLoginInfoDirty} />}
            {currentPage === "CommInfo" && <CommInfo />}
            {currentPage === "PaymentInfo" && <PaymentInfo />}
          </div>
        </div>
      </section>

      {pendingTab != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-6 shadow-lg" style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}>
            <h3 className="text-[15px] font-extrabold text-healthsource-dark">Unsaved Changes</h3>
            <p className="mt-2 text-[13px] text-healthsource-gray">
              You have unsaved changes that will be lost if you leave this page.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelLeaveLoginInfo}
                className="min-h-11 rounded-md border border-healthsource-lightLavender px-4 text-[13px] font-bold text-healthsource-dark transition-colors hover:bg-healthsource-lightGray"
              >
                Stay
              </button>
              <button
                type="button"
                onClick={confirmLeaveLoginInfo}
                className="min-h-11 rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--hs-red)" }}
              >
                Leave Page
              </button>
            </div>
          </div>
        </div>
      )}

      {showMyInfoDiscardModal && myInfoEditActions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-6 shadow-lg" style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}>
            <h3 className="text-[15px] font-extrabold text-healthsource-dark">Unsaved Changes</h3>
            <p className="mt-2 text-[13px] text-healthsource-gray">
              You have unsaved changes. Are you sure you want to discard them?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowMyInfoDiscardModal(false)}
                className="min-h-11 rounded-md border border-healthsource-lightLavender px-4 text-[13px] font-bold text-healthsource-dark transition-colors hover:bg-healthsource-lightGray"
              >
                Keep Editing
              </button>
              <button
                type="button"
                onClick={() => {
                  myInfoEditActions.cancel();
                  setShowMyInfoDiscardModal(false);
                }}
                className="min-h-11 rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--hs-red)" }}
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
